import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Mail, Mic, Phone, Plus, Sparkles, Trash2, Zap } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { useToast } from '@/components/Toast'
import { useCurrentUser, useSuggestedBriefingTopics } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import type {
  Briefing,
  BriefingDeliveryMethod,
  BriefingTopic,
  BriefingTopicCategory,
} from '@/types'

const CHANNEL_LABELS: Record<BriefingDeliveryMethod, string> = {
  email: 'Email',
  dashboard: 'Dashboard',
  voice: 'Voice call',
  slack: 'Slack',
}

const CATEGORIES: BriefingTopicCategory[] = [
  'industry',
  'competitor',
  'company',
  'market',
  'technology',
  'local',
  'custom',
]

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export function BriefingSettings() {
  const me = useCurrentUser()
  const queryClient = useQueryClient()
  const toast = useToast()
  const suggest = useSuggestedBriefingTopics()

  const [enabled, setEnabled] = useState(false)
  const [topics, setTopics] = useState<BriefingTopic[]>([])
  const [delivery, setDelivery] = useState<BriefingDeliveryMethod[]>(['email', 'dashboard'])
  const [briefingTime, setBriefingTime] = useState('07:00')
  const [briefingTimezone, setBriefingTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
  )
  const [mobilePhone, setMobilePhone] = useState('')

  // Topic creation row state
  const [newLabel, setNewLabel] = useState('')
  const [newQuery, setNewQuery] = useState('')
  const [newCategory, setNewCategory] = useState<BriefingTopicCategory>('custom')

  // Voice-to-text topic add (browser SpeechRecognition)
  const [listening, setListening] = useState(false)

  useEffect(() => {
    if (!me.data) return
    setEnabled(me.data.briefingEnabled ?? false)
    setTopics(me.data.briefingTopics ?? [])
    setDelivery((me.data.briefingDelivery as BriefingDeliveryMethod[]) ?? ['email', 'dashboard'])
    setBriefingTime(me.data.briefingTime ?? '07:00')
    if (me.data.briefingTimezone) setBriefingTimezone(me.data.briefingTimezone)
    setMobilePhone(me.data.mobilePhone ?? '')
  }, [me.data])

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        apiClient.post('/briefing/settings', {
          enabled,
          topics,
          delivery,
          briefingTime,
          briefingTimezone,
          mobilePhone: mobilePhone || null,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user', 'me'] }),
  })

  const preview = useMutation({
    mutationFn: () =>
      unwrap<{ briefing: Briefing }>(apiClient.post('/briefing/preview')),
    onError: (e) => toast.error('Preview failed', (e as Error).message),
  })

  const testEmail = useMutation({
    mutationFn: () =>
      unwrap<{ sent: boolean; to: string }>(
        apiClient.post('/briefing/test-email'),
      ),
    onSuccess: (data) => toast.success('Email sent', `Delivered to ${data.to}`),
    onError: (e) => toast.error('Email send failed', (e as Error).message),
  })

  const testCall = useMutation({
    mutationFn: () =>
      unwrap<{ calling: string }>(apiClient.post('/briefing/test-call')),
    onSuccess: (data) => toast.success('Calling now', `Dialing ${data.calling}`),
    onError: (e) => toast.error('Call failed', (e as Error).message),
  })

  const addTopic = () => {
    if (!newLabel.trim() || !newQuery.trim()) return
    setTopics((prev) => [
      ...prev,
      {
        id: newId(),
        label: newLabel.trim(),
        query: newQuery.trim(),
        category: newCategory,
        enabled: true,
        addedVia: 'manual',
        addedAt: new Date().toISOString(),
      },
    ])
    setNewLabel('')
    setNewQuery('')
    setNewCategory('custom')
  }

  const toggleTopic = (id: string) => {
    setTopics((prev) =>
      prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t)),
    )
  }

  const removeTopic = (id: string) => {
    setTopics((prev) => prev.filter((t) => t.id !== id))
  }

  const toggleDelivery = (ch: BriefingDeliveryMethod) => {
    setDelivery((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    )
  }

  const acceptSuggestion = (topic: Omit<BriefingTopic, 'id' | 'enabled' | 'addedVia' | 'addedAt'>) => {
    setTopics((prev) => [
      ...prev,
      {
        ...topic,
        id: newId(),
        enabled: true,
        addedVia: 'ai_suggested',
        addedAt: new Date().toISOString(),
      },
    ])
  }

  const handleVoiceAdd = () => {
    const w = window as unknown as {
      SpeechRecognition?: new () => SpeechRecognitionLike
      webkitSpeechRecognition?: new () => SpeechRecognitionLike
    }
    const SR = w.SpeechRecognition ?? w.webkitSpeechRecognition
    if (!SR) {
      alert('Voice input is not supported in this browser.')
      return
    }
    const recognition = new SR()
    recognition.lang = 'en-US'
    recognition.interimResults = false
    recognition.continuous = false
    setListening(true)
    recognition.onresult = (event: SpeechResultEvent) => {
      const text = event.results?.[0]?.[0]?.transcript ?? ''
      if (text) {
        setNewLabel(text)
        setNewQuery(`${text} news`)
        setNewCategory('custom')
      }
    }
    recognition.onerror = () => setListening(false)
    recognition.onend = () => setListening(false)
    recognition.start()
  }

  return (
    <JarvisCard id="briefing">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
          Daily Briefing
        </h2>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="accent-jarvis-blue"
          />
          {enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>

      <p className="text-xs text-jarvis-muted mb-4">
        Wake up to a 2-minute brief on the topics you care about. Jarvis pulls fresh news,
        digests it, and delivers via your chosen channel.
      </p>

      {/* Delivery + timing */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <span className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5 block">
            Delivery time
          </span>
          <div className="flex gap-2">
            <input
              type="time"
              value={briefingTime}
              onChange={(e) => setBriefingTime(e.target.value)}
              className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
            />
            <input
              type="text"
              value={briefingTimezone}
              onChange={(e) => setBriefingTimezone(e.target.value)}
              placeholder="America/Chicago"
              className="flex-1 bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
            />
          </div>
        </div>
        <div>
          <span className="text-xs uppercase tracking-widest text-jarvis-muted mb-1.5 block">
            Mobile (for voice call)
          </span>
          <input
            type="tel"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.target.value)}
            placeholder="+15125550123"
            className="w-full bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
          />
        </div>
      </div>

      <div className="mb-6">
        <span className="text-xs uppercase tracking-widest text-jarvis-muted mb-2 block">
          Delivery channels
        </span>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(CHANNEL_LABELS) as BriefingDeliveryMethod[]).map((ch) => (
            <label
              key={ch}
              className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border cursor-pointer transition-colors ${
                delivery.includes(ch)
                  ? 'border-jarvis-blue/60 bg-jarvis-blue/10 text-jarvis-blue'
                  : 'border-jarvis-border text-jarvis-muted hover:text-jarvis-text'
              }`}
            >
              <input
                type="checkbox"
                checked={delivery.includes(ch)}
                onChange={() => toggleDelivery(ch)}
                className="hidden"
              />
              {CHANNEL_LABELS[ch]}
            </label>
          ))}
        </div>
      </div>

      {/* Topic list */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-widest text-jarvis-muted">
            Your topics ({topics.length})
          </span>
          <button
            onClick={() => suggest.mutate()}
            disabled={suggest.isPending}
            className="text-xs flex items-center gap-1 px-2 py-1 rounded-md text-jarvis-blue hover:bg-jarvis-blue/10 disabled:opacity-50"
            title="Get AI suggestions based on your company"
          >
            <Sparkles size={12} />
            {suggest.isPending ? 'Suggesting…' : 'Suggest topics'}
          </button>
        </div>
        {topics.length === 0 ? (
          <div className="text-sm text-jarvis-muted py-4 text-center border border-dashed border-jarvis-border rounded-lg">
            No topics yet. Add one below or click "Suggest topics".
          </div>
        ) : (
          <div className="space-y-1.5">
            {topics.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-3 px-3 py-2 rounded-md bg-jarvis-surface-2 border border-jarvis-border/50"
              >
                <input
                  type="checkbox"
                  checked={t.enabled}
                  onChange={() => toggleTopic(t.id)}
                  className="accent-jarvis-blue"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.label}</div>
                  <div className="text-xs text-jarvis-muted truncate">
                    {t.category} · "{t.query}"
                  </div>
                </div>
                <button
                  onClick={() => removeTopic(t.id)}
                  className="text-jarvis-muted hover:text-jarvis-red transition-colors"
                  aria-label="Remove topic"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI suggestions */}
      {suggest.data && suggest.data.length > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-jarvis-blue/30 bg-jarvis-blue/5">
          <div className="text-xs uppercase tracking-widest text-jarvis-blue mb-2 flex items-center gap-1.5">
            <Sparkles size={12} /> Suggested for your business
          </div>
          <div className="space-y-1.5">
            {suggest.data
              .filter((s) => !topics.some((t) => t.label === s.label))
              .map((s) => (
                <div key={s.label} className="flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="truncate">{s.label}</span>
                    <span className="text-xs text-jarvis-muted ml-2">{s.category}</span>
                  </div>
                  <button
                    onClick={() => acceptSuggestion(s)}
                    className="text-xs px-2 py-1 rounded-md text-jarvis-blue hover:bg-jarvis-blue/10"
                  >
                    Add
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add new topic */}
      <div className="border-t border-jarvis-border pt-4">
        <div className="text-xs uppercase tracking-widest text-jarvis-muted mb-2">
          Add a topic
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_140px_auto] gap-2">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (e.g. Competitor: Nexstar)"
            className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
          />
          <input
            value={newQuery}
            onChange={(e) => setNewQuery(e.target.value)}
            placeholder="Search query"
            className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
          />
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as BriefingTopicCategory)}
            className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-jarvis-blue/60"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            onClick={addTopic}
            disabled={!newLabel.trim() || !newQuery.trim()}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-jarvis-blue text-jarvis-surface hover:bg-jarvis-blue-bright disabled:opacity-50 text-sm transition-colors"
          >
            <Plus size={14} /> Add
          </button>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            onClick={handleVoiceAdd}
            disabled={listening}
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md text-jarvis-muted hover:text-jarvis-text disabled:opacity-50"
            title="Add a topic by speaking"
          >
            <Mic size={12} className={listening ? 'text-jarvis-red animate-pulse' : ''} />
            {listening ? 'Listening…' : 'Add by voice'}
          </button>
        </div>
      </div>

      {/* Preview */}
      <div className="mt-6 border-t border-jarvis-border pt-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="text-sm font-medium">Preview today's briefing</p>
            <p className="text-xs text-jarvis-muted mt-0.5">
              Runs the full pipeline right now. Shows you exactly what you'll receive.
            </p>
          </div>
          <button
            onClick={() => preview.mutate()}
            disabled={preview.isPending || topics.filter((t) => t.enabled).length === 0}
            className="text-sm flex items-center gap-2 px-4 py-2 rounded-lg bg-jarvis-blue hover:bg-jarvis-blue-bright text-white transition-colors disabled:opacity-50"
          >
            <Zap size={14} />
            {preview.isPending ? 'Generating…' : 'Generate preview'}
          </button>
        </div>

        {topics.length === 0 && !preview.data && (
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface-2 p-4 text-sm">
            <p className="text-xs uppercase tracking-widest text-jarvis-blue mb-2">
              Example briefing
            </p>
            <p className="text-jarvis-muted leading-relaxed">
              Good morning, Alex. Here's your Monday intelligence digest.{' '}
              <strong className="text-jarvis-text">Electric Vehicles:</strong> GM announced
              a $2B investment in solid-state battery production, citing faster charging and
              40% higher range vs. current lithium-ion.{' '}
              <strong className="text-jarvis-text">Competitor: Nexstar:</strong> Q1 earnings
              beat by 12% — local TV ad revenue rebounded on election-year spend. That's
              your briefing. You have 3 calls scheduled and 2 open tasks. Have a sharp morning.
            </p>
            <p className="text-xs text-jarvis-muted mt-3">
              Add topics above to start receiving your own daily intelligence.
            </p>
          </div>
        )}

        {preview.data?.briefing && (
          <div className="rounded-lg border border-jarvis-border bg-jarvis-surface-2 p-4 text-sm whitespace-pre-wrap leading-relaxed">
            {preview.data.briefing.content.text}
          </div>
        )}

        {preview.data?.briefing && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => testEmail.mutate()}
              disabled={testEmail.isPending}
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-jarvis-border hover:border-jarvis-blue/60 hover:bg-jarvis-surface transition disabled:opacity-50"
            >
              <Mail size={12} />
              {testEmail.isPending ? 'Sending…' : 'Send to my email now'}
            </button>
            {me.data?.mobilePhone && me.data?.acsPhoneNumber && (
              <button
                onClick={() => testCall.mutate()}
                disabled={testCall.isPending}
                className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-jarvis-border hover:border-jarvis-blue/60 hover:bg-jarvis-surface transition disabled:opacity-50"
              >
                <Phone size={12} />
                {testCall.isPending ? 'Dialing…' : 'Call me now'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {save.isPending ? 'Saving…' : save.isSuccess ? 'Saved' : 'Save briefing settings'}
        </button>
      </div>
    </JarvisCard>
  )
}

// Minimal SpeechRecognition typings for the browser-only voice add flow —
// the standard lib type isn't shipped in TS DOM lib in older versions.
interface SpeechResultEvent {
  results: ArrayLike<ArrayLike<{ transcript: string }>>
}
interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  continuous: boolean
  start: () => void
  onresult: ((event: SpeechResultEvent) => void) | null
  onerror: ((event: unknown) => void) | null
  onend: ((event: unknown) => void) | null
}
